import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmReceipt } from '@shared/models/bcm-receipt';
import { BcmPaymentType } from '@shared/models/person';
import { TsePaymentType } from '@shared/models/tse/TsePaymentType';
import { roundCurrency } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';

const CASH_REGISTER_CANCELLATION_PAYMENT_TYPES: Partial<Record<BcmPaymentType, TsePaymentType>> = {
    [BcmPaymentType.Cash]: TsePaymentType.CASH,
    [BcmPaymentType.EcCard]: TsePaymentType.EC_CARD,
    [BcmPaymentType.CreditCard]: TsePaymentType.CREDIT_CARD,
};

export function isCancellationMode(receiptToCancel?: BcmReceipt | null): boolean {
    return !!receiptToCancel?.id;
}

export function mapReceiptPaymentTypeToTse(paymentType: BcmPaymentType): TsePaymentType | null {
    return CASH_REGISTER_CANCELLATION_PAYMENT_TYPES[paymentType] ?? null;
}

export function isCashRegisterCancellationPaymentType(paymentType: BcmPaymentType): boolean {
    return mapReceiptPaymentTypeToTse(paymentType) != null;
}

export function resolveCheckoutTotalSum(options: {
    receiptToCancel?: BcmReceipt;
    positions: InvoicePosition[];
    totalAmount?: number;
    getTotal: (positions: InvoicePosition[], sign: number) => number;
}): number {
    const { receiptToCancel, positions, totalAmount, getTotal } = options;

    if (!isCancellationMode(receiptToCancel)) {
        const sign = 1;
        const calculatedTotal = getTotal(positions, sign);
        const explicitTotal = totalAmount != null ? roundCurrency(Math.abs(totalAmount) * sign) : null;
        return explicitTotal != null && explicitTotal !== 0 ? explicitTotal : calculatedTotal;
    }

    const fromPositions = getTotal(positions, -1);
    if (fromPositions !== 0) {
        return fromPositions;
    }

    if (receiptToCancel.revenue != null) {
        return roundCurrency(-Math.abs(receiptToCancel.revenue));
    }

    if (totalAmount != null) {
        return roundCurrency(-Math.abs(totalAmount));
    }

    return 0;
}

/** Ergebnis beim Schließen: null = Formular behalten, Array = Beleg abgeschlossen. */
export function resolveCheckoutCloseResult(
    receipt: BcmReceipt | undefined | null,
    updatedPositions: InvoicePosition[]
): InvoicePosition[] | null {
    return receipt?.id ? updatedPositions : null;
}

export function hasReceiptCreationError(
    receiptErrorMessages: string[] | undefined,
    receipt: BcmReceipt | undefined | null
): boolean {
    return (receiptErrorMessages?.length ?? 0) > 0 && !receipt?.id;
}

/** Backend meldet: Beleg ist bereits bei der TSE, nur das Speichern scheiterte – ein Retry würde erneut signieren. */
export function isTseDocumentCreatedError(err: unknown): boolean {
    return (err as { error?: { tseDocumentCreated?: unknown } })?.error?.tseDocumentCreated === true;
}

export function shouldPrintAfterCheckout(options: {
    isCancellationMode: boolean;
    dataShouldPrint?: boolean;
    printCancellationReceipt?: boolean;
    paymentType: TsePaymentType;
}): boolean {
    if (options.isCancellationMode) {
        return options.paymentType === TsePaymentType.CASH && !!options.printCancellationReceipt;
    }

    return !!options.dataShouldPrint;
}

/** Storno-Flow liefert den erzeugten Beleg; Kassiervorgang liefert aktualisierte Positionen. */
export function resolveCheckoutDialogClosePayload(
    receipt: BcmReceipt | undefined | null,
    updatedPositions: InvoicePosition[],
    isCancellationMode: boolean,
): BcmReceipt | InvoicePosition[] | null {
    if (!receipt?.id) {
        return null;
    }

    if (isCancellationMode) {
        return receipt;
    }

    return updatedPositions;
}
